'use client';

import { useEffect, useRef, useState } from 'react';
import { RecentConversations } from './recent-conversations';
import { Transcript, type TranscriptTurn } from './transcript';

export interface ChatSessionViewProps {
  /** Whether Satoshi is currently composing a reply (between user-send and his first onMessage). */
  isReplying: boolean;
  /** True while ending or session-ended phase (fade-out state). */
  isEnding: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
  onNewSession: () => void;
  /** Remaining seconds (shared budget with voice). Used to derive ~messages-left. */
  remaining: number | null;
  budget: number;
  /** Per-message debit cost — for converting `remaining` into message count. */
  chatTurnSeconds: number;
  turns: TranscriptTurn[];
  online: boolean;
  /**
   * Non-blocking warning string when conversation provisioning failed
   * (signed-in users only). Renders a small indicator in the sidebar so
   * the user knows this session won't appear in history.
   */
  persistError?: string | null;
  /**
   * Current conversation row id, set after `provisionConversation` round-
   * trips. Null for anonymous users (no persistence) and during the
   * brief window before the create call returns. Used by the sidebar
   * "RECENT" list to exclude the active conversation.
   */
  currentConversationId: string | null;
  /**
   * Click handler for a row in the sidebar "RECENT" list. Wires through
   * to voice-panel's `handleResume`.
   */
  onResume: (id: string) => void;
}

/**
 * Chat-mode session layout. Mirrors SessionView's two-column shape
 * (sidebar + main) so the navbar / chrome story is consistent across
 * modes. The main area replaces the orb with a chat-style transcript
 * + input pair.
 *
 * Input is disabled while Satoshi is composing a reply (between the
 * user's send and the first onMessage from the agent) — matches the
 * ChatGPT / Claude pattern of "one turn at a time" rather than letting
 * the user race the SDK with stacked sends.
 */
export function ChatSessionView({
  isReplying,
  isEnding,
  onSend,
  onStop,
  onNewSession,
  remaining,
  budget,
  chatTurnSeconds,
  turns,
  online,
  persistError,
  currentConversationId,
  onResume,
}: ChatSessionViewProps) {
  const [input, setInput] = useState('');
  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll transcript to bottom on new messages.
  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns.length]);

  // Refocus input after Satoshi finishes replying so the user can keep
  // typing without reaching for the mouse.
  useEffect(() => {
    if (!isReplying && !isEnding) inputRef.current?.focus();
  }, [isReplying, isEnding]);

  const canSend = !isReplying && !isEnding && input.trim().length > 0;

  const handleSend = () => {
    const text = input.trim();
    if (!canSend) return;
    onSend(text);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const messagesLeft = remaining !== null && chatTurnSeconds > 0
    ? Math.max(0, Math.floor(remaining / chatTurnSeconds))
    : null;

  return (
    <div className="flex mt-[65px] h-[calc(100vh-113px)]">

      {/* ── Left sidebar ──────────────────────────────────────────── */}
      <aside className="hidden md:flex flex-col w-56 lg:w-64 border-r border-outline-variant bg-surface-container-lowest shrink-0">
        <div className="px-5 pt-6 pb-4 border-b border-outline-variant">
          <div className="flex items-center gap-2.5 mb-1">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/logo.png" alt="" className="w-5 h-5 object-contain shrink-0" />
            <span className="font-semibold text-xs text-on-surface tracking-tight">Just Ask Satoshi</span>
          </div>
          <div className="flex items-center gap-1.5 pl-7">
            <div className="w-1 h-1 rounded-full bg-primary-bright" />
            <span className="font-mono text-[9px] tracking-widest text-primary">CHAT LIVE</span>
          </div>
        </div>

        <div className="flex-1 px-3 pt-4 space-y-1">
          <button
            onClick={onNewSession}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 bg-primary-bright text-on-primary font-mono text-[11px] font-bold tracking-widest hover:bg-primary active:scale-95 transition-all"
          >
            <span className="text-base leading-none">+</span>
            NEW SESSION
          </button>

          <div className="pt-3 pb-1">
            <p className="font-mono text-[9px] tracking-widest text-outline px-3 mb-2">CURRENT</p>
            <div className="flex items-center gap-2.5 px-3 py-2.5 bg-surface-container border border-outline-variant">
              <div className="w-1.5 h-1.5 rounded-full bg-primary-bright shrink-0" />
              <span className="font-mono text-[10px] tracking-widest text-on-surface">This chat</span>
            </div>
          </div>

          {persistError && (
            <div className="px-3 mt-3" title={persistError}>
              <p className="font-mono text-[9px] tracking-widest text-amber-400 border border-amber-400/30 px-3 py-2">
                ⚠ HISTORY NOT SAVING
              </p>
            </div>
          )}

          <RecentConversations
            currentConversationId={currentConversationId}
            onResume={onResume}
          />
        </div>

        {/* Sidebar footer — message budget */}
        {messagesLeft !== null && (() => {
          const pct = Math.max(0, Math.min(100, (remaining! / budget) * 100));
          const isCritical = pct < 15;
          const isWarning = pct < 35;
          return (
            <div className={`px-5 py-4 border-t transition-colors duration-500 ${
              isCritical ? 'border-error-text/40 bg-error-text/5' :
              isWarning  ? 'border-primary/30' :
              'border-outline-variant'
            }`}>
              <p className={`font-mono text-[9px] tracking-widest mb-2 transition-colors ${
                isCritical ? 'text-error-text' : 'text-outline'
              }`}>
                {isCritical ? 'ALMOST OUT' : 'MESSAGES LEFT'}
              </p>
              <div className={`overflow-hidden mb-2 transition-all duration-500 ${
                isCritical ? 'h-[3px]' : isWarning ? 'h-[2px]' : 'h-px'
              } bg-outline-variant`}>
                <div
                  className={`h-full transition-all duration-1000 ${
                    isCritical ? 'bg-error-text animate-pulse' :
                    isWarning  ? 'bg-amber-400' :
                    'bg-primary-bright'
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className={`font-mono text-[11px] tracking-widest font-bold transition-colors ${
                isCritical ? 'text-error-text animate-pulse' :
                isWarning  ? 'text-amber-400' :
                'text-on-surface-variant'
              }`}>
                {messagesLeft} {messagesLeft === 1 ? 'MSG' : 'MSGS'}
              </p>
            </div>
          );
        })()}
      </aside>

      {/* ── Main chat area ────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 bg-surface-container-low">
        <div
          className="absolute inset-0 pointer-events-none z-0"
          style={{
            background:
              'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(247,147,26,0.008) 3px, rgba(247,147,26,0.008) 4px)',
          }}
        />

        <div className="relative z-10 flex-1 flex flex-col min-h-0">
          {/* Transcript area — fills available space, scrolls. */}
          <div
            ref={chatRef}
            className="flex-1 overflow-y-auto px-4 md:px-8 py-6"
          >
            {turns.length === 0 ? (
              <EmptyChatState />
            ) : (
              <div className="max-w-2xl mx-auto">
                <Transcript turns={turns} />
                {isReplying && (
                  <div className="mt-4 border-l-2 border-primary-bright pl-4 opacity-60">
                    <p className="font-mono text-[10px] tracking-widest text-primary mb-1">SATOSHI</p>
                    <p className="text-sm text-on-surface-variant italic">composing&hellip;</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Input + end-session row, anchored to bottom. */}
          {!isEnding && (
            <div className="border-t border-outline-variant bg-surface px-4 md:px-8 py-4">
              <div className="max-w-2xl mx-auto flex items-end gap-3">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  disabled={isReplying || isEnding}
                  placeholder={isReplying ? 'Satoshi is replying…' : 'Ask Satoshi something…'}
                  aria-label="Message Satoshi"
                  className="flex-1 resize-none bg-surface-container-low border border-outline-variant text-on-surface placeholder:text-outline px-4 py-3 text-sm focus:border-primary focus:outline-none disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!canSend}
                  className="font-mono text-[11px] tracking-widest font-bold bg-primary-bright text-on-primary px-5 py-3 hover:bg-primary active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  SEND
                </button>
              </div>
              <div className="max-w-2xl mx-auto mt-3 flex items-center justify-between">
                <button
                  onClick={onStop}
                  className="font-mono text-[10px] tracking-widest text-outline hover:text-error-text transition-colors"
                >
                  &#x2715; END SESSION
                </button>
                {!online && (
                  <p className="font-mono text-[10px] tracking-wider text-primary/70 border border-primary/30 px-3 py-1">
                    &#9888; Connection unstable
                  </p>
                )}
              </div>
            </div>
          )}

          {isEnding && (
            <div className="flex items-center justify-center pb-12">
              <p className="font-mono text-sm tracking-widest text-on-surface-variant">
                ENDING SESSION...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyChatState() {
  return (
    <div className="max-w-md mx-auto mt-12 text-center">
      <p className="font-mono text-[10px] tracking-widest text-primary mb-3">CHAT WITH SATOSHI</p>
      <p className="text-sm text-on-surface-variant leading-relaxed">
        Type a question and press Enter. Satoshi knows the whitepaper, every BitcoinTalk post,
        the Hal Finney and Martti Malmi correspondence, and the current state of the network.
      </p>
    </div>
  );
}
